import {
  ApolloClient,
  ApolloProvider,
  from,
  InMemoryCache,
  useQuery,
} from "@apollo/client";
import { Box, MantineProvider, Stack } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { GET_USER } from "./apis/lambda/GraphQL/User/queries";
import "./App.scss";
import {
  hideTiktokBannerAtom,
  isTiktokLinkedAtom,
  setupCompleteAtom,
  userInfoAtom,
} from "./atoms";
import Footer from "./components/Footer";
import BrandNavBar from "./components/NavBar";
import PendingApprovalBanner from "./components/PendingApprovalBanner/PendingApprovalBanner";
import ProfileCompletionBanner from "./components/ProfileCompletionBanner/ProfileCompletionBanner";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import TiktokConnectBanner from "./components/TiktokConnectBanner/TiktokConnectBanner";
import UnapprovedUserGuard from "./components/UnapprovedUserGuard";
import { environment } from "./constants/environment";
import { mantineTheme } from "./mantineTheme";
import NotFoundPage from "./pages/404NotFoundPageTODO/NotFoundPage";
import ContactUsPage from "./pages/AboutPages/ContactUsPage";
import PrivacyPolicyPage from "./pages/AboutPages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/AboutPages/TermsOfServicePage";
import EmbeddedSandbox from "./pages/ADMINPages/EmbeddedSandbox";
import CollabsPage from "./pages/CollabsPage/CollabsPage";
import DiscoverCreatorsPage from "./pages/DiscoverPage/DiscoverPage";
import LandingPage from "./pages/LandingPage/LandingPage";
import ChangePasswordPage from "./pages/LoginFlow/ForgotPasswordPage/ChangePasswordPage/ChangePasswordPage";
import ForgotPasswordPage from "./pages/LoginFlow/ForgotPasswordPage/ForgotPasswordPage";
import LoginPage from "./pages/LoginFlow/LoginPage/LoginPage";
import SignupCompletePage from "./pages/LoginFlow/SignupPage/SignupCompletePage";
import SignupPage from "./pages/LoginFlow/SignupPage/SignupPage";
import VerifyEmailPage from "./pages/LoginFlow/VerifyEmailPage/VerifyEmailPage";
import PasscodePage from "./pages/PasscodePage/PasscodePage";
import ProductsPage from "./pages/ProductsPage/ProductsPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import StripeReturnPage from "./pages/Stripe/StripeReturnPage";
import TikTokPage from "./pages/TikTok/TikTokPage";
import { authLink, errorLink, httpLink } from "./utils/apollo/links";
import { decodeJWTFromCookie, isLoggedIn } from "./utils/jwtUtils";

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, authLink, httpLink]),
});

function App() {
  // Jotai State
  const [userInfo, setUserInfo] = useAtom(userInfoAtom);
  const [setupComplete, setSetupComplete] = useAtom(setupCompleteAtom);
  const [hideTiktokBanner, setHideTiktokBanner] = useAtom(hideTiktokBannerAtom);
  const [isTiktokLinked, setIsTiktokLinked] = useAtom(isTiktokLinkedAtom);

  useEffect(() => {
    if (isLoggedIn()) {
      setUserInfo(decodeJWTFromCookie());
    } else {
      setUserInfo(null);
      // Reset persisted state on logout
      setSetupComplete(false);
      setHideTiktokBanner(false);
      setIsTiktokLinked(false);
    }
  }, [setUserInfo, setSetupComplete, setHideTiktokBanner, setIsTiktokLinked]);

  // GraphQL
  const { data } = useQuery(GET_USER, {
    variables: {
      email: userInfo?.email ?? "",
    },
    skip: !userInfo || (setupComplete && (isTiktokLinked || hideTiktokBanner)),
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data?.user) {
      const { validated, name, description, tiktokData, userType, brandId } =
        data.user;

      // Check if setup is complete based on user type
      let isComplete = false;
      if (userType === "BRAND_USER") {
        // For brands: check email, brand profile (name/description), and products
        // Note: We can't easily check products here without another query,
        // so we'll just check email and profile for the banner decision
        isComplete = !!(validated && name && description && brandId);
      } else {
        // For creators: check email, profile (name/description), and optionally TikTok
        isComplete = !!(validated && name && description);
      }

      if (isComplete && !setupComplete) {
        setSetupComplete(true);
      }

      if (tiktokData && !isTiktokLinked) {
        setIsTiktokLinked(true);
      }
    }
  }, [
    data,
    setupComplete,
    isTiktokLinked,
    setSetupComplete,
    setIsTiktokLinked,
  ]);

  const handleDismissTiktokBanner = () => {
    setHideTiktokBanner(true);
  };

  const isBrand = userInfo?.userType === "BRAND_USER";

  // For brand users, check if they're awaiting approval
  const isAwaitingApproval = isBrand && data?.user &&
    (data.user.approved === false || !data.user.validated);

  // Show profile banner only if setup incomplete AND not awaiting approval
  const showProfileBanner = userInfo && !setupComplete && !isAwaitingApproval;

  const showTiktokBanner =
    userInfo &&
    !isBrand &&
    setupComplete &&
    !isTiktokLinked &&
    !hideTiktokBanner;

  return (
    <ApolloProvider client={client}>
      <MantineProvider theme={mantineTheme}>
        <Notifications />
        <BrowserRouter>
          <Stack mih="100vh" gap={"0"} bg={"gray.0"}>
            <BrandNavBar />

            <PendingApprovalBanner />
            {showProfileBanner && <ProfileCompletionBanner />}
            {showTiktokBanner && (
              <TiktokConnectBanner onDismiss={handleDismissTiktokBanner} />
            )}

            {/* Role Toggle */}
            {/* <Box
              bg="white"
              py={"sm"}
              style={{ borderBottom: "1px solid #e9ecef" }}
            >
              <Container size="xl">
                <Group justify="center">
                  <Text
                    size="sm"
                    fw={500}
                    c={!isBrand ? "secondary" : "dimmed"}
                  >
                    Creator
                  </Text>
                  <Switch
                    size="md"
                    checked={isBrand.isBrand}
                    onChange={(event) =>
                      handleToggleChange(event.currentTarget.checked)
                    }
                    color="primary"
                    thumbIcon={
                      isBrand ? (
                        <Text size="xs" fw={700}>
                          B
                        </Text>
                      ) : (
                        <Text size="xs" fw={700}>
                          C
                        </Text>
                      )
                    }
                  />
                  <Text
                    size="sm"
                    fw={500}
                    c={isBrand ? "primary" : "dimmed"}
                  >
                    Brand
                  </Text>
                </Group>
              </Container>
            </Box> */}

            <Box component="main">
              <Routes>
                {/* Passcode page is publicly accessible */}
                <Route path="/passcode" element={<PasscodePage />} />

                {/* All other routes are protected */}
                <Route path="/" element={<LandingPage />} />

                <Route
                  path="/discover"
                  element={
                    <ProtectedRoute>
                      <UnapprovedUserGuard>
                        <DiscoverCreatorsPage />
                      </UnapprovedUserGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/products"
                  element={
                    <ProtectedRoute>
                      <UnapprovedUserGuard>
                        <ProductsPage />
                      </UnapprovedUserGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/collabs"
                  element={
                    <ProtectedRoute>
                      <UnapprovedUserGuard>
                        <CollabsPage />
                      </UnapprovedUserGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile/:id"
                  element={
                    <ProtectedRoute>
                      <UnapprovedUserGuard>
                        <ProfilePage />
                      </UnapprovedUserGuard>
                    </ProtectedRoute>
                  }
                />

                <Route path="/login">
                  <Route
                    index
                    element={
                      <ProtectedRoute>
                        <LoginPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="change-password"
                    element={<ChangePasswordPage />}
                  />
                </Route>

                <Route path="/signup">
                  <Route
                    index
                    element={
                      <ProtectedRoute>
                        <SignupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="complete"
                    element={
                      <ProtectedRoute>
                        <SignupCompletePage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route path="tiktok" element={<TikTokPage />} />

                <Route path="/confirm-email" element={<VerifyEmailPage />} />

                <Route
                  path="/stripe/return"
                  element={
                    <ProtectedRoute>
                      <StripeReturnPage />
                    </ProtectedRoute>
                  }
                />

                {/* Text Pages */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/contact" element={<ContactUsPage />} />

                {environment.MODE !== "production" && (
                  <Route path="graphql" element={<EmbeddedSandbox />} />
                )}

                <Route path="*" element={<NotFoundPage />} />

                {/* <Route index element={<RoleSelectionPage />} />
                <Route path="/discover" element={<DiscoverCreatorsPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/collabs" element={<CollabsPage />} />
                <Route path="/brand" element={<BrandCardExample />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/test" element={<CreatorCardExample />} />
                <Route path="/login" element={<LoginPage />} />

                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />

                <Route path="*" element={<NotFoundPage />} /> */}
              </Routes>
            </Box>

            <Footer />
          </Stack>
        </BrowserRouter>
      </MantineProvider>
    </ApolloProvider>
  );
}

export default App;
